import { useState, useCallback } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { Table, Pagination } from '@treezor/design-system';

import { useCardTransactionsData } from '@/features/cards/hooks/CardTransactions/useCardTransactionsData';
import {
  ICardTransaction,
  IDigitizedCard,
  TCardTransactionFormFilters,
} from '@/features/cards/store/models/card.model';

import { ICursor } from '@/store/types/search.types';
import { EmptyState } from '@/ui-kit/templates/EmptyState/EmptyState';
import { CardTransactionsFilters } from '@/features/cards/components/CardTransactionsTable/CardTransactionsFilters';
import { CardTransactionSettings } from '@/features/cards/components/CarTransactionSettings/CardTransactionSettings';
import { CardTransactionDetails } from '@/features/cards/components/CardTransactionsTable/CardTransactionDetails';

interface ICardTransactionsTableProps {
  isLoadingTransactions: boolean;
  cardTransactions?: ICardTransaction[];
  digitizedCards?: IDigitizedCard[];
  cursor?: ICursor;
  onFiltersChange?: ({ filters, cursorId }: { filters?: TCardTransactionFormFilters; cursorId?: string }) => void;
  className?: string;
}

export const CardTransactionsTable = ({
  cardTransactions,
  cursor,
  digitizedCards,
  isLoadingTransactions,
  onFiltersChange,
  className,
}: ICardTransactionsTableProps) => {
  const { t } = useTranslation();
  const [selectedTransaction, setSelectedTransaction] = useState<ICardTransaction | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleRowClick = useCallback((transaction: ICardTransaction) => {
    setSelectedTransaction(transaction);
    setIsSidebarOpen(true);
  }, []);

  const handleCloseSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  const { rows, headers, pagination, hasFilters, clearFilters, fields, setSearchValue } = useCardTransactionsData({
    cardTransactions,
    digitizedCards,
    isLoading: isLoadingTransactions,
    cursor,
    onFiltersChange,
    onRowClick: handleRowClick,
  });
  const handleFilterByPaymentId = useCallback(
    (paymentId: string) => {
      if (setSearchValue) {
        setSearchValue('paymentId', paymentId);
      }
      setIsSidebarOpen(false);
    },
    [setSearchValue],
  );

  if ((cardTransactions || [])?.length === 0 && !hasFilters) {
    return (
      <div className="text-center mt-6">
        {isLoadingTransactions ? (
          <Table columns={headers} rows={rows} className="bg-primary" />
        ) : (
          <EmptyState
            icon="no_transaction_v1"
            size="l"
            title={t('pages.currentProfiles.cards.transactions.noTransaction')}
            state={t('pages.currentProfiles.cards.transactions.noTransactionDescription')}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between">
          <CardTransactionsFilters fields={fields} clearFunction={hasFilters ? clearFilters : undefined} />
          <CardTransactionSettings />
        </div>
        {cardTransactions?.length > 0 ? (
          <div className={clsx('flex flex-col', className)}>
            <Table hasStickyHeader columns={headers} rows={rows} className="bg-primary overflow-auto lightScrollBar" />
            {(!!cursor?.next || !!cursor?.prev) && (
              <Pagination
                {...pagination}
                isFirstButtonVisible
                isLastButtonVisible={false}
                isPagesVisible={false}
                className="flex justify-end w-full"
                isDisabled={false}
              />
            )}
          </div>
        ) : (
          <Table
            columns={headers}
            rows={[]}
            emptyComponent={
              <EmptyState
                icon="no_transaction_v1"
                size="l"
                title={t('pages.currentProfiles.cards.transactions.emptyTransaction')}
                state={t('pages.currentProfiles.cards.transactions.emptyTransactionDescription')}
                action={t('pages.currentProfiles.cards.filters.clear')}
                buttonIcon={null}
                onClick={clearFilters}
              />
            }
          />
        )}
      </div>
      <CardTransactionDetails
        isOpen={isSidebarOpen}
        transaction={selectedTransaction}
        onClose={handleCloseSidebar}
        onFilterByPaymentId={handleFilterByPaymentId}
      />
    </>
  );
};
